import { ModuleData } from '@library/store/modules/modules.interface';
import { createActionGroup, props } from '@ngrx/store';
import {
  GetHomeConfigPayload,
  HomeCoachsData,
  HomeData,
  HomeSyncPayload,
  SetHomeConfigPayload,
} from '../homes/homes.interface';
import { APIResponseError } from '../interfaces/erros';
import {
  CreateRoom,
  GetMeasureCsvPayload,
  NewRoomCreated,
  RoomData,
  RoomModulesUpdate,
  RoomNameUpdate,
  RoomOffsetUpdatePayload,
  SetTrueTemperaturePayload,
} from './rooms.interface';

export const RoomsActions = createActionGroup({
  source: 'Rooms',
  events: {
    // Rooms
    'Get Rooms': props<{ payload: RoomData[] }>(),
    'Get Rooms Success': props<{ payload: HomeData[]; modules?: ModuleData[] }>(),
    'Get Rooms Failure': props<{ payload: any }>(),

    'Select Room': props<{ payload: string }>(),

    'Update Rooms Status': props<{ payload: HomeSyncPayload }>(),
    'Update Rooms Get Config': props<{ payload: GetHomeConfigPayload }>(),
    'Update Rooms Set Config': props<{ payload: SetHomeConfigPayload }>(),

    // True temperature
    'Set True Temperature': props<{ payload: SetTrueTemperaturePayload }>(),
    'Set True Temperature Success': props<{ payload: SetTrueTemperaturePayload }>(),
    'Set True Temperature Failure': props<{ payload: any }>(),

    // Measure CSV
    'Get Measure Csv': props<{ payload: GetMeasureCsvPayload }>(),
    'Get Measure Csv Success': props<{ payload: Blob; info: GetMeasureCsvPayload }>(),
    'Get Measure Csv Failure': props<{ payload: any }>(),

    'Get Room Measure Csv': props<{ payload: GetMeasureCsvPayload }>(),
    'Get Room Measure Csv Success': props<{ payload: Blob; info: GetMeasureCsvPayload }>(),
    'Get Room Measure Csv Failure': props<{ payload: any }>(),

    // Room name
    'Update Room Name': props<{ payload: RoomNameUpdate; header?: Record<string, string> }>(),
    'Update Room Name Success': props<{ payload: RoomNameUpdate }>(),
    'Update Room Name Failure': props<{ payload: APIResponseError }>(),

    // Modules in room
    'Remove Module From Room': props<{ payload: RoomModulesUpdate }>(),
    'Add Module To Room': props<{ payload: RoomModulesUpdate; header?: Record<string, string> }>(),

    // Create room
    'Create New Room': props<{ payload: CreateRoom; header?: Record<string, string> }>(),
    'Create New Room Success': props<{ payload: NewRoomCreated }>(),
    'Create New Room Failure': props<{ payload: APIResponseError }>(),

    // Delete measurements
    'Delete Measurements': props<{ payload: any }>(),
    'Delete Measurements Success': props<{ payload: any }>(),
    'Delete Measurements Failure': props<{ payload: any }>(),

    // Delete room
    'Delete Room': props<{ payload: { home_id: string; room_id: string } }>(),
    'Delete Room Success': props<{ payload: { home_id: string; room_id: string } }>(),
    'Delete Room Failure': props<{ payload: any }>(),

    // Measure offset
    'Update Room Measure Offset': props<{ payload: RoomOffsetUpdatePayload[] }>(),
    'Update Room Measure Offset Success': props<{ payload: RoomOffsetUpdatePayload[] }>(),
    'Update Room Measure Offset Failure': props<{ payload: any }>(),

    // Home coachs
    'Get Rooms Home Coachs Success': props<{ payload: HomeCoachsData }>(),
    'Get Rooms Home Coachs Failure': props<{ payload: any }>(),
  },
});

export type RoomActionUnion = ReturnType<typeof RoomsActions[keyof typeof RoomsActions]>;

// Types unitaires
export type GetRooms = ReturnType<typeof RoomsActions.getRooms>;
export type GetRoomsSuccess = ReturnType<typeof RoomsActions.getRoomsSuccess>;
export type GetRoomsFailure = ReturnType<typeof RoomsActions.getRoomsFailure>;

export type SelectRoom = ReturnType<typeof RoomsActions.selectRoom>;

export type UpdateRoomsStatus = ReturnType<typeof RoomsActions.updateRoomsStatus>;
export type UpdateRoomsGetConfig = ReturnType<typeof RoomsActions.updateRoomsGetConfig>;
export type UpdateRoomsSetConfig = ReturnType<typeof RoomsActions.updateRoomsSetConfig>;

export type SetTrueTemperature = ReturnType<typeof RoomsActions.setTrueTemperature>;
export type SetTrueTemperatureSuccess = ReturnType<typeof RoomsActions.setTrueTemperatureSuccess>;
export type SetTrueTemperatureFailure = ReturnType<typeof RoomsActions.setTrueTemperatureFailure>;

export type GetMeasureCsv = ReturnType<typeof RoomsActions.getMeasureCsv>;
export type GetMeasureCsvSuccess = ReturnType<typeof RoomsActions.getMeasureCsvSuccess>;
export type GetMeasureCsvFailure = ReturnType<typeof RoomsActions.getMeasureCsvFailure>;

export type GetRoomMeasureCsv = ReturnType<typeof RoomsActions.getRoomMeasureCsv>;
export type GetRoomMeasureCsvSuccess = ReturnType<typeof RoomsActions.getRoomMeasureCsvSuccess>;
export type GetRoomMeasureCsvFailure = ReturnType<typeof RoomsActions.getRoomMeasureCsvFailure>;

export type UpdateRoomName = ReturnType<typeof RoomsActions.updateRoomName>;
export type UpdateRoomNameSuccess = ReturnType<typeof RoomsActions.updateRoomNameSuccess>;
export type UpdateRoomNameFailure = ReturnType<typeof RoomsActions.updateRoomNameFailure>;

export type RemoveModuleFromRoom = ReturnType<typeof RoomsActions.removeModuleFromRoom>;
export type AddModuleToRoom = ReturnType<typeof RoomsActions.addModuleToRoom>;

export type CreateNewRoom = ReturnType<typeof RoomsActions.createNewRoom>;
export type CreateNewRoomSuccess = ReturnType<typeof RoomsActions.createNewRoomSuccess>;
export type CreateNewRoomFailure = ReturnType<typeof RoomsActions.createNewRoomFailure>;

export type DeleteMeasurements = ReturnType<typeof RoomsActions.deleteMeasurements>;
export type DeleteMeasurementsSuccess = ReturnType<typeof RoomsActions.deleteMeasurementsSuccess>;
export type DeleteMeasurementsFailure = ReturnType<typeof RoomsActions.deleteMeasurementsFailure>;

export type DeleteRoom = ReturnType<typeof RoomsActions.deleteRoom>;
export type DeleteRoomSuccess = ReturnType<typeof RoomsActions.deleteRoomSuccess>;
export type DeleteRoomFailure = ReturnType<typeof RoomsActions.deleteRoomFailure>;

export type UpdateRoomMeasureOffset = ReturnType<typeof RoomsActions.updateRoomMeasureOffset>;
export type UpdateRoomMeasureOffsetSuccess = ReturnType<typeof RoomsActions.updateRoomMeasureOffsetSuccess>;
export type UpdateRoomMeasureOffsetFailure = ReturnType<typeof RoomsActions.updateRoomMeasureOffsetFailure>;

export type GetRoomsHomeCoachsSuccess = ReturnType<typeof RoomsActions.getRoomsHomeCoachsSuccess>;
export type GetRoomsHomeCoachsFailure = ReturnType<typeof RoomsActions.getRoomsHomeCoachsFailure>;

export type RoomsActionUnion = ReturnType<typeof RoomsActions[keyof typeof RoomsActions]>;
